import React, { useEffect, useMemo, useReducer } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import TextInput from "../../components/Common/TextInput";
import { useAccounts } from "../../DataStore/AccountsContext";
import { useTag } from "../../DataStore/TagContext";
import { useTheme } from "../../DataStore/ThemeContext";
import Notification from "../../components/Common/Notification";
import { useRefreshTrans } from "../../DataStore/useRefreshTrans";
import ajaxLiability from "../../util/remote/ajaxLiability";
import VendorSelector from "../../components/Transaction/VendorSelector";
import SectionTitle from "../../components/Common/SectionTitle";
import AccountingHint from "../../components/Ledger/AccountingHint";

const initialState = {
  loading: false,

  liabilityAccount: null,   // option object
  paymentAccount: null,     // option object
  amount: "",
  description: "",
  transactionRef: "",
  transactionDate: "",

  payee: null,              // option object
  tag: null,                // option object
};

const reducer = (state, action) => {
  switch (action.type) {
    case "RESET_PARTIAL":
      return { ...state, amount: "", description: "" };
    default:
      return { ...state, [action.type]: action.value };
  }
};

// 🔹 helper: consistent responsive columns (1 / 2 / 3 per row)
const Col = ({ children }) => <div className="col-12 col-md-6 col-xl-4">{children}</div>;

const NewLiabilityPage = () => {
  const { openSettings, setNotify, currentNotify } = useTheme();
  const refreshBalances = useRefreshTrans();
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    getReceiptAccounts,
    receiptAccountListing,
    liabilityAccountListing,
    getLiabilityAccounts,
  } = useAccounts();

  const { getTagListing, tagListing } = useTag();

  useEffect(() => {
    getReceiptAccounts();
    getLiabilityAccounts();
    getTagListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const liabilityOptions = useMemo(() => {
    const opts = [];
    if (Array.isArray(liabilityAccountListing)) {
      liabilityAccountListing.forEach((a) =>
        opts.push({ label: `${a.account_number} ${a.account_name}`, value: a.account_id })
      );
    }
    return opts;
  }, [liabilityAccountListing]);

  const depositOptions = useMemo(() => {
    const opts = [];
    if (Array.isArray(receiptAccountListing)) {
      receiptAccountListing.forEach((a) =>
        opts.push({ label: `${a.account_number} ${a.account_name}`, value: a.account_id })
      );
    }
    return opts;
  }, [receiptAccountListing]);

  const tagOptions = useMemo(() => {
    const opts = [
      { label: "Create New Project...", value: "add" },
      { label: "- No project -", value: "0" },
    ];
    if (Array.isArray(tagListing)) {
      tagListing.forEach((t) => opts.push({ label: t.tag_name, value: t.tag_id }));
    }
    return opts;
  }, [tagListing]);

  const journalHint = useMemo(() => {
    const depositTo = state.paymentAccount?.label || "Funds Deposit Account";
    const liability = state.liabilityAccount?.label || "Liability Account";
    const creditor = state.payee?.label || "Creditor";
    return `Liability journal: Debit ${depositTo}, Credit ${liability} (${creditor}). When you repay, Debit ${liability} and Credit Cash/Bank.`;
  }, [state.paymentAccount, state.liabilityAccount, state.payee]);

  const onConfirm = async (event) => {
    event.preventDefault();

    if (Number(state.amount) <= 0) {
      setNotify(<Notification type="danger" message="Enter amount and try again." />);
      return;
    }

    if (!state.liabilityAccount?.value || !state.paymentAccount?.value) {
      setNotify(
        <Notification type="danger" message="Select both liability account and funds deposit account." />
      );
      return;
    }

    if (!state.payee?.value) {
      setNotify(<Notification type="danger" message="Select payee - i.e. the origin of the loan." />);
      return;
    }

    dispatch({ type: "loading", value: true });

    const server_response = await ajaxLiability.addLiability(
      state.paymentAccount.value,
      state.liabilityAccount.value,
      state.amount,
      state.transactionDate,
      state.description,
      state.payee.value,
      state.transactionRef,
      state.tag?.value || "0"
    );

    dispatch({ type: "loading", value: false });

    if (server_response.status === "OK") {
      dispatch({ type: "RESET_PARTIAL" });
      setNotify(<Notification type="success" message={server_response.message} />);
      refreshBalances();
    } else {
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Add Loan (Current Liability)"
          description="Funds received to facilitate business operations but are not generated from the business. Eg. Capital, short loans etc."
          iconClass="fa fa-briefcase"
        />

        <div className="mb-3">
          <AccountingHint hint={journalHint} />
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <form method="post" onSubmit={onConfirm}>
              {/* ---------- LIABILITY ACCOUNTS ---------- */}
              <div className="row mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-university"
                    title="Liability Accounts"
                    note="Select the liability account and where the borrowed funds were deposited."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Liability Account"
                    placeholder="Select liability account..."
                    leftIcon="fa fa-university"
                    options={liabilityOptions}
                    onChange={(item) => dispatch({ type: "liabilityAccount", value: item })}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getLiabilityAccounts(true)} />}
                    loading={!liabilityAccountListing}
                    value={state.liabilityAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="select"
                    label="Funds Deposit Account"
                    placeholder="Select funds deposit account..."
                    leftIcon="fa fa-university"
                    options={depositOptions}
                    onChange={(item) => dispatch({ type: "paymentAccount", value: item })}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getReceiptAccounts(true)} />}
                    loading={!receiptAccountListing}
                    value={state.paymentAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="number"
                    label="Amount"
                    placeholder="Amount"
                    leftIcon="fa fa-credit-card"
                    value={state.amount}
                    onChange={(e) => dispatch({ type: "amount", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>
              </div>

              {/* ---------- DETAILS ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-info-circle"
                    title="Loan Details"
                    note="Capture transaction details for audit trail and repayment tracking."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="date"
                    label="Transaction Date"
                    placeholder="Transaction Date"
                    leftIcon="fa fa-calendar-alt"
                    value={state.transactionDate}
                    onChange={(e) => dispatch({ type: "transactionDate", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="text"
                    label="Transaction Ref"
                    placeholder="Transaction Ref"
                    leftIcon="fa fa-hashtag"
                    value={state.transactionRef}
                    onChange={(e) => dispatch({ type: "transactionRef", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <VendorSelector
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Vendor", "NewVendor");
                        return;
                      }
                      dispatch({ type: "payee", value: item });
                    }}
                    placeholder="Select creditor..."
                    label="Creditor"
                    value={state.payee}
                    wrap
                  />
                </Col>
              </div>

              {/* Description full width */}
              <div className="row" style={{ rowGap: 12 }}>
                <div className="col-12">
                  <TextInput
                    type="textarea"
                    label="Funds Description"
                    placeholder="Where did the funds come from? What is the purpose of the funds?"
                    leftIcon="fa fa-list"
                    value={state.description}
                    onChange={(e) => dispatch({ type: "description", value: e.target.value })}
                    required
                    wrap
                  />
                </div>
              </div>

              {/* ---------- CLASSIFICATION ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-tag"
                    title="Classification"
                    note="Optional: attach a project to improve reporting."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Tag Transaction"
                    placeholder={state.tag?.label || "Transaction is not tagged..."}
                    leftIcon="fa fa-tag"
                    options={tagOptions}
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Project", "NewTag", null, "Create Project");
                        return;
                      }
                      dispatch({ type: "tag", value: item });
                    }}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getTagListing(true)} />}
                    loading={!tagListing}
                    value={state.tag}
                    wrap
                  />
                </Col>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-end mt-3" style={{ gap: 10 }}>
                <button type="submit" className="btn btn-primary btn-lg" disabled={state.loading}>
                  {state.loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check-circle mr-2" />
                      Save Liability
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-light btn-lg"
                  onClick={() => dispatch({ type: "RESET_PARTIAL" })}
                  disabled={state.loading}
                >
                  <i className="fa fa-undo mr-2" />
                  Clear Amount & Note
                </button>
              </div>

              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Tip: Loans increase liabilities until you repay them.
              </div>
            </form>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default NewLiabilityPage;
